import { useEffect, useMemo, useState } from 'react'
import { Landmark, RefreshCw } from 'lucide-react'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { StatusBadge } from '../../components/PageParts'
import { Field } from '../master/masterShared'
import { SettingsHeader, SettingsTabs } from './settingsShared'
import { initiateMono, listMonoConnections } from '../../api/mono'
import { me as getMe } from '../../api/auth'

export default function MonoConnections() {
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [rows, setRows] = useState([])
  const [me, setMe] = useState(null)

  const tenantId = useMemo(() => {
    return (
      me?.tenant_id ??
      me?.tenantId ??
      me?.tenant?.tenant_id ??
      me?.tenant?.id ??
      1
    )
  }, [me])

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')

  async function load() {
    setErr('')
    setLoading(true)
    try {
      const who = await getMe()
      setMe(who)

      setName(who?.full_name || who?.name || '')
      setEmail(who?.email || '')

      const data = await listMonoConnections({
        tenant_id: who?.tenant_id ?? 1,
        limit: 20,
      })
      setRows(data?.rows || [])
    } catch (e) {
      setErr(e?.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  async function onConnect() {
    setErr('')
    setBusy(true)
    try {
      const payload = {
        tenant_id: tenantId,
        name: (name || '').trim(),
        email: (email || '').trim(),
      }
      const out = await initiateMono(payload)

      if (!out?.mono_url) throw new Error('No mono_url returned from server')
      window.open(out.mono_url, '_blank', 'noopener,noreferrer')

      setTimeout(() => load(), 2500)
    } catch (e) {
      setErr(e?.message || String(e))
    } finally {
      setBusy(false)
    }
  }

  const formatType = (value) =>
    String(value || '')
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/^\w/, (m) => m.toUpperCase())
  const statusTone = (value) => {
    const s = String(value || '').toLowerCase()
    if (s.includes('available') || s.includes('active')) return 'good'
    if (s.includes('pending') || s.includes('sync')) return 'warn'
    return 'muted'
  }
  const statusLabel = (value) => {
    const s = String(value || '').replace(/_/g, ' ').toLowerCase()
    return s ? s[0].toUpperCase() + s.slice(1) : 'Unknown'
  }

  return (
    <div className="ci-page">
      <SettingsTabs />
      <SettingsHeader
        title="Bank connections"
        meta="Link your business bank accounts through Mono, for bank reconciliation."
        actions={<button type="button" className="ci-btn ci-btn-ghost" onClick={load} disabled={busy || loading}><RefreshCw size={15} />Refresh</button>}
      />
      {err && <ErrorBanner message={err} />}

      <div className="set-stack">
        <div className="ci-form-meta-card mf-form">
          <div className="mf-card-title">Connect a bank account</div>
          <div className="ci-form-meta-top two">
            <Field label="Account holder’s name"><input value={name} onChange={(e) => setName(e.target.value)} /></Field>
            <Field label="Email"><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
          </div>
          <div className="set-body">
            <p className="doc-hint">Mono opens in a new tab to sign in to the bank. ASYS only keeps the last 4 digits of the account number and the connection details, never the full number or any passwords.</p>
            <div className="set-actions">
              <button data-write type="button" className="ci-btn ci-btn-primary" onClick={onConnect} disabled={busy || !(name || '').trim() || !(email || '').trim()}>
                <Landmark size={14} />{busy ? 'Opening Mono…' : 'Connect a bank'}
              </button>
            </div>
          </div>
        </div>

        <div className="ci-table-wrap">
          <div className="ci-table-scroll">
            <table className="ci-table">
              <thead>
                <tr><th>Bank</th><th>Account name</th><th>Account</th><th className="hide-sm">Type</th><th className="hide-sm">Currency</th><th>Status</th><th className="hide-sm">Last updated</th></tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="7"><Spinner label="Loading your bank connections…" /></td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan="7"><div className="ci-empty">No bank accounts connected yet.</div></td></tr>
                ) : rows.map((r) => (
                  <tr key={r.id}>
                    <td>{r.institution_name || '–'}</td>
                    <td>{r.account_name || '–'}</td>
                    <td className="set-nowrap">•••• {r.account_number_last4 || '–'}</td>
                    <td className="hide-sm">{formatType(r.account_type) || '–'}</td>
                    <td className="hide-sm">{r.currency || '–'}</td>
                    <td><StatusBadge label={statusLabel(r.data_status)} tone={statusTone(r.data_status)} /></td>
                    <td className="hide-sm ci-muted-cell">{r.last_sync_at || '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && rows.length > 0 && <div className="ci-pagination"><span className="ci-page-info">{rows.length} {rows.length === 1 ? 'account' : 'accounts'} connected</span></div>}
        </div>
      </div>
    </div>
  )
}
